import axios from "axios";
import { ApiClient } from "./api";
import ApiPath from "../constants/apiPath";

const uploadChunkWithFallback = async ({
  url,
  chunk,
  controller,
  onUploadProgress,
}) => {
  // Fetch does not expose upload progress events in the browser.
  // We report chunk completion as progress so the UI still advances.
  const response = await fetch(url, {
    method: "PUT",
    body: chunk,
    signal: controller.signal,
  });

  if (!response.ok) {
    throw new Error(`Chunk upload failed with status ${response.status}`);
  }

  if (onUploadProgress) {
    onUploadProgress({ loaded: chunk.size, total: chunk.size });
  }

  return {
    headers: {
      etag: response.headers.get("ETag"),
      ETag: response.headers.get("ETag"),
    },
  };
};

export const abortMultipartUploadSession = async (session) => {
  if (!session?.uploadId || !session?.key) {
    return;
  }

  try {
    await ApiClient.post(ApiPath.adminApiPath.UPLOAD_MULTIPART_ABORT, {
      uploadId: session.uploadId,
      upload_id: session.uploadId,
      key: session.key,
      fileKey: session.key,
      file_key: session.key,
    });
  } catch (error) {
    console.error("Error aborting multipart upload:", error);
  }
};

export const uploadMultipartFile = async ({
  file,
  chunkSize = 100 * 1024 * 1024,
  multipartUploadRef,
  onProgress,
}) => {
  const partsCount = Math.ceil(file.size / chunkSize);

  if (onProgress) {
    onProgress(0);
  }

  try {
    const initiateResponse = await ApiClient.post(
      ApiPath.adminApiPath.UPLOAD_MULTIPART_INITIATE,
      {
        originalName: file.name,
        fileType: file.type || "application/octet-stream",
      },
    );

    const initiateData = initiateResponse?.data;

    if (!initiateData?.data?.uploadId || !initiateData?.data?.fileKey) {
      throw new Error(initiateData?.message || "Failed to initiate upload");
    }

    const uploadId = initiateData.data.uploadId;
    const fileKey = initiateData.data.fileKey;

    if (multipartUploadRef) {
      multipartUploadRef.current = {
        uploadId,
        key: fileKey,
        controllers: [],
      };
    }

    const urlsResponse = await ApiClient.post(
      ApiPath.adminApiPath.UPLOAD_MULTIPART_PRESIGNED_URLS,
      {
        uploadId,
        fileKey,
        partsCount,
      },
    );

    const urlsData = urlsResponse?.data;
    const urls = urlsData?.data?.presignedUrls || [];

    if (!urls.length) {
      throw new Error(urlsData?.message || "Failed to get presigned URLs");
    }

    const uploadedParts = [];

    for (let index = 0; index < urls.length; index++) {
      const current = urls[index];
      const start = index * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);
      const controller = new AbortController();
      const uploadUrl = current?.url;

      if (multipartUploadRef?.current?.controllers) {
        multipartUploadRef.current.controllers.push(controller);
      }

      if (!uploadUrl) {
        throw new Error(
          `Missing presigned URL for part ${current.partNumber || index + 1}`,
        );
      }

      if (
        typeof window !== "undefined" &&
        window.location.protocol === "https:" &&
        uploadUrl.startsWith("http://")
      ) {
        throw new Error(
          "Presigned upload URL is HTTP while the app is running on HTTPS. The browser will block this as mixed content.",
        );
      }

      let uploadHost = "";

      try {
        uploadHost = new URL(uploadUrl).host;
      } catch (error) {
        uploadHost = "unknown-host";
      }

      let uploadResponse;

      try {
        uploadResponse = await uploadChunkWithFallback({
          url: uploadUrl,
          chunk,
          controller,
          onUploadProgress: (progressEvent) => {
            if (!onProgress || !progressEvent?.loaded) {
              return;
            }

            const uploadedSoFar = start + progressEvent.loaded;
            const progressPercentage = Math.min(
              100,
              Math.round((uploadedSoFar / file.size) * 100),
            );

            onProgress(progressPercentage);
          },
        });
      } catch (error) {
        const partNumber = current.partNumber || index + 1;
        const isNetworkStyleError =
          error?.message === "Network Error" ||
          error?.message === "Failed to fetch" ||
          error?.name === "TypeError";

        if (isNetworkStyleError) {
          throw new Error(
            `Part ${partNumber} upload could not reach ${uploadHost}. This is usually a presigned URL, CORS, mixed-content, or network/proxy issue.`,
          );
        }

        throw new Error(
          `Part ${partNumber} upload failed: ${error.message || "Unknown error"}`,
        );
      }

      const etag =
        uploadResponse?.headers?.etag?.replaceAll('"', "") ||
        uploadResponse?.headers?.ETag?.replaceAll('"', "");

      if (!etag) {
        throw new Error(
          `Missing ETag for uploaded part ${current.partNumber || index + 1}. Check that S3 CORS exposes the ETag header.`,
        );
      }

      uploadedParts.push({
        ETag: etag,
        PartNumber: current.partNumber || current.part_number || index + 1,
      });

      if (onProgress) {
        const progressPercentage = Math.min(
          100,
          Math.round((end / file.size) * 100),
        );

        onProgress(progressPercentage);
      }
    }

    const completeResponse = await ApiClient.post(
      ApiPath.adminApiPath.UPLOAD_MULTIPART_COMPLETE,
      {
        uploadId,
        fileKey,
        parts: uploadedParts,
      },
    );

    const completeData = completeResponse?.data;

    if (!completeData) {
      throw new Error("Failed to complete upload");
    }

    if (multipartUploadRef) {
      multipartUploadRef.current = null;
    }

    return {
      uploadId,
      fileKey,
      fileUrl:
        completeData?.s3_url ||
        completeData?.file_url ||
        completeData?.fileUrl ||
        completeData?.location ||
        completeData?.data?.s3_url ||
        completeData?.data?.location ||
        completeData?.data?.file_url ||
        completeData?.data?.fileUrl ||
        completeData?.data?.file_key ||
        fileKey,
    };
  } catch (error) {
    const activeSession = multipartUploadRef?.current;

    if (activeSession?.controllers?.length) {
      activeSession.controllers.forEach((controller) => controller.abort());
    }

    if (activeSession?.uploadId && activeSession?.key) {
      await abortMultipartUploadSession(activeSession);
    }

    if (multipartUploadRef) {
      multipartUploadRef.current = null;
    }

    if (
      error?.name === "AbortError" ||
      error?.name === "CanceledError" ||
      axios.isCancel(error)
    ) {
      throw new Error("Upload canceled.");
    }

    throw error;
  }
};
